// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable react/prop-types */
import React from 'react';
import { graphql } from 'gatsby';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { GLULayout } from '../../layouts/glu';
import DoubleTextBlock from '../../components/glu/organisms/double-text-block';
import HeroWithVideo from '../../components/glu/organisms/hero-with-video';
import TiltedCardContainer from '../../components/glu/organisms/tilted-card';
import PageSection from '../../components/glu/organisms/page-section';
import SessionCardContainer from '../../components/glu/organisms/session-card-container';
import ServiceCardContainer from '../../components/glu/organisms/service-card-container';
import ArticleNav from '../../components/glu/organisms/article-nav';
import { Head } from '../../components/templates/head';
import {
	getOrganizationSeoSchema,
	getBreadcrumbsSeoSchema,
	getImageSeoSchema,
} from '../../utils/seo-schema';
import { pageTypes } from '../../utils/url-helpers';

const Homepage = ({ data, location }) => {
	const {
		eLearningSessionCards,
		hero: heroContent,
		intro,
		eLearningTitle,
		liveSessionTitle,
		liveWebinarSessions,
		otherServicesTitle,
		otherServices,
		locale,
	} = data.datoCmsGluHomepage;
	const { nodes: articles } = data.allDatoCmsBlogPage;
	const { mainLogo } = data.allDatoCmsGluLogo.nodes[0];
	const metaContent = {
		noIndex: data.datoCmsGluHomepage.noIndex,
		noFollow: data.datoCmsGluHomepage.noFollow,
		metaInformation: data.datoCmsGluHomepage.metaInformation,
		fallbackTitle: data.datoCmsGluHomepage.eLearningTitle,
		fallbackDescription: '',
		canonicalUrl: data.datoCmsGluHomepage?.canonicalUrl || location?.href,
	};

	const seoSchemas = {
		organization: getOrganizationSeoSchema({ data, url: location?.origin }),
		breadcrumbs: getBreadcrumbsSeoSchema({
			pageType: pageTypes.gluHome,
			location,
		}),
		image: getImageSeoSchema(mainLogo?.url),
	};

	const sortedELearningSessionCards = eLearningSessionCards.sort(
		(a, b) => a.originalId - b.originalId
	);

	return (
		<GoogleReCaptchaProvider
			reCaptchaKey={process.env.GATSBY_RECAPTCHA_KEY}
		>
			<Head metaContent={metaContent} seoSchemas={seoSchemas} />
			{articles.length > 0 && <ArticleNav articleData={articles} />}

			<HeroWithVideo
				hero={heroContent}
				logo={mainLogo}
				privateVideoHash="9ace720501"
				locale={locale}
			/>
			<DoubleTextBlock textBlocks={intro.doubleTextBlock[0]} />
			<PageSection titleBlock={eLearningTitle.value} logo={mainLogo}>
				<TiltedCardContainer
					cards={sortedELearningSessionCards}
					locale={locale}
				/>
			</PageSection>
			<PageSection
				theme="White"
				titleBlock={liveSessionTitle.value}
				logo={mainLogo}
			>
				<SessionCardContainer
					liveWebinarSessions={liveWebinarSessions}
					locale={locale}
				/>
			</PageSection>
			<PageSection theme="Dark" titleBlock={otherServicesTitle.value}>
				<ServiceCardContainer cards={otherServices} />
			</PageSection>
		</GoogleReCaptchaProvider>
	);
};

const WrappedHomepage = ({ data, location }) => (
	<GLULayout locale={data.datoCmsGluHomepage.locale}>
		<Homepage data={data} location={location} />
	</GLULayout>
);
export default WrappedHomepage;

export const query = graphql`
	query gluHomepage($locale: String!) {
		datoCmsGluHomepage(locale: { eq: $locale }) {
			locale
			noIndex
			noFollow
			metaInformation {
				title
				description
				image {
					url
				}
				twitterCard
			}
			intro {
				doubleTextBlock {
					firstBlock {
						value
					}
					secondBlock {
						value
					}
				}
			}
			eLearningTitle {
				value
			}
			liveWebinarSessions {
				highlightedLiveWebinar {
					title
					dateAndTime
					slug
				}
				image {
					gatsbyImageData(placeholder: DOMINANT_COLOR)
					alt
					url
				}
				upcomingLiveWebinars {
					title
					dateAndTime
					slug
				}
			}
			liveSessionTitle {
				value
			}
			eLearningSessionCards {
				id
				originalId
				title
				image {
					gatsbyImageData
					alt
				}
				description {
					value
				}
				ctaLink
				ctaText
			}
			hero {
				vimeoLink {
					thumbnailUrl
					width
					height
					providerUid
					provider
					title
				}
				videoOverlayText
				trustpilotReview
				mainTitle {
					value
				}
				mainSubheading {
					value
				}
			}
			otherServices {
				title
				image {
					gatsbyImageData
					alt
				}
				ctaLink
				ctaText
				content {
					value
				}
				videoLinks {
					id
					title
					vimeoLink {
						thumbnailUrl
						width
						height
						providerUid
						provider
						title
					}
				}
			}
			otherServicesTitle {
				value
			}
		}
		allDatoCmsBlogPage(
			limit: 5
			sort: { order: DESC, fields: meta___createdAt }
			filter: { locale: { eq: $locale } }
		) {
			nodes {
				title
				slug
				categories {
					slug
				}
			}
		}
		allDatoCmsGluLogo {
			nodes {
				mainLogo {
					url
					alt
				}
			}
		}
	}
`;
